/**
 * BO Hub — Point d'entrée du back-office code2roues (Phase 29).
 * Route : /bo
 *
 * Cette page :
 *  - Vérifie l'auth (redirige vers /login si non connecté)
 *  - Affiche les stats globales du back-office (élèves, enseignants, leçons)
 *  - Propose 4 tuiles vers les sections : Secrétariat, Mon Planning, Inbox Admin, Seed démo
 *  - Adapte l'accès selon le rôle (admin voit tout, enseignant voit son planning, etc.)
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, Calendar, Inbox, Sparkles, GraduationCap, ArrowRight,
  Briefcase, ShieldCheck, AlertCircle, ExternalLink, BookOpen, Car,
} from 'lucide-react';
import api from '../../api';

const BO_BLUE = '#1d4ed8';

export default function BoHubPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seedResult, setSeedResult] = useState(null);
  const [voitureSeedLoading, setVoitureSeedLoading] = useState(false);
  const [voitureSeedResult, setVoitureSeedResult] = useState(null);
  const [inboundReady, setInboundReady] = useState(false);

  useEffect(() => {
    document.title = 'Back-office · Code 2 Roues';
    (async () => {
      try {
        const me = await api.getMe();
        setUser(me);
        // Tenter stats secrétariat (échoue si pas admin/secrétaire)
        try {
          const s = await api.boGetSecretariatStats();
          setStats(s);
        } catch (_) { /* silencieux — rôle insuffisant */ void 0; }
        // Vérifier si l'inbound Resend est réellement en production
        // (MX de réception vérifié + webhook secret présent). Sinon on cache la tuile.
        try {
          const status = await api.boInboundStatus();
          setInboundReady(status?.mode === 'production');
        } catch (_) { void 0; }
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          // Pas connecté → redirect propre avec preservation de la route
          navigate('/login', { state: { from: '/bo' } });
          return;
        }
        // Autre erreur → juste log, la page reste utilisable
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleSeed = async () => {
    setSeedResult(null);
    try {
      const r = await api.boSeedDemo();
      setSeedResult(r);
      // Refresh stats
      try {
        const s = await api.boGetSecretariatStats();
        setStats(s);
      } catch (_) { /* stats already loaded, ignore */ }
    } catch (e) {
      setError(e.message || 'Seed impossible');
    }
  };

  const handleSeedVoiture = async () => {
    if (voitureSeedLoading) return;
    if (!window.confirm('Importer les 103 articles voiture en base ? Idempotent (les articles déjà présents seront skippés).')) return;
    setVoitureSeedResult(null);
    setError(null);
    setVoitureSeedLoading(true);
    try {
      const r = await api.boSeedVoitureContent();
      setVoitureSeedResult(r);
    } catch (e) {
      setError(e.message || 'Import contenu voiture impossible');
    } finally {
      setVoitureSeedLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center text-foreground/50 text-sm">
        Chargement du back-office…
      </div>
    );
  }

  const role = user?.role || 'guest';
  const isAdmin = role === 'admin';
  const isSecretaire = role === 'secretaire' || isAdmin;
  const isEnseignant = role === 'enseignant' || isAdmin;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="bo-hub">
      {/* Bandeau contextuel */}
      <div
        className="rounded-2xl border-2 p-5 sm:p-6 mb-6 sm:mb-8"
        style={{ borderColor: BO_BLUE, background: 'rgba(29, 78, 216, 0.06)' }}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <Briefcase className="w-7 h-7 flex-shrink-0 mt-1" style={{ color: BO_BLUE }} />
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] mb-1 flex items-center gap-2"
              style={{ color: BO_BLUE }}
            >
              <Sparkles className="w-3 h-3" /> Back-office · Phase 29
            </div>
            <h1
              className="text-2xl sm:text-3xl font-black tracking-tight text-foreground leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Bienvenue, {user?.name || user?.email}.
            </h1>
            <p className="text-xs sm:text-sm text-foreground/70 mt-2 leading-relaxed">
              Interface d&apos;administration importée depuis <strong>codevoiture.fr</strong>. Rôle actuel :{' '}
              <span className="font-black uppercase" style={{ color: BO_BLUE }}>
                {role}
              </span>.
              {' · '}Namespace <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded">/api/bo/*</code>
            </p>
          </div>
        </div>
      </div>

      {/* Stats globales (visible si secrétaire+) */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="bo-stats">
          <StatCard label="Élèves" value={stats.eleves} icon={Users} color={BO_BLUE} testid="stat-eleves" />
          <StatCard label="Enseignants" value={stats.enseignants} icon={GraduationCap} color="#059669" testid="stat-enseignants" />
          <StatCard label="Leçons aujourd&apos;hui" value={stats.lessons_today} icon={Calendar} color="#dc2626" testid="stat-lessons-today" />
          <StatCard label="À venir" value={stats.lessons_future} icon={Calendar} color="#f59e0b" testid="stat-lessons-future" />
        </div>
      )}

      {/* Grille sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {isSecretaire && (
          <>
            <SectionCard
              to="/bo/secretariat/eleves"
              icon={Users}
              title="Élèves"
              subtitle="Créer, modifier, supprimer les fiches élèves. Suivi des heures."
              testid="section-eleves"
            />
            <SectionCard
              to="/bo/secretariat/enseignants"
              icon={GraduationCap}
              title="Enseignants"
              subtitle="Gérer l'équipe pédagogique, spécialités, couleurs planning."
              testid="section-enseignants"
            />
            <SectionCard
              to="/bo/secretariat/planning"
              icon={Calendar}
              title="Planning global"
              subtitle="Vue secrétariat : toutes les leçons, tous les enseignants."
              testid="section-planning-global"
            />
          </>
        )}

        {isEnseignant && (
          <SectionCard
            to="/bo/mon-planning"
            icon={Calendar}
            title="Mon planning"
            subtitle="Vos leçons à venir. Ajouter, reporter, annuler, faire le bilan."
            testid="section-my-planning"
          />
        )}

        {(role === 'eleve' || isAdmin) && (
          <SectionCard
            to="/bo/eleve"
            icon={BookOpen}
            title="Mon espace élève"
            subtitle="Planning perso, historique des leçons, bilans reçus, progression."
            testid="section-eleve"
          />
        )}

        {isAdmin && inboundReady && (
          <SectionCard
            to="/bo/admin/inbox"
            icon={Inbox}
            title="Boîte mail admin"
            subtitle="Emails reçus via Resend inbound. Répondre, archiver, supprimer."
            testid="section-inbox"
            badge="Beta"
          />
        )}

        {isAdmin && !inboundReady && (
          <div
            className="rounded-xl border-2 border-dashed border-border/60 bg-card/50 p-5 opacity-70"
            data-testid="section-inbox-disabled"
          >
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white/70 flex-shrink-0"
                style={{ background: '#94a3b8' }}
              >
                <Inbox className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/40 flex items-center gap-2">
                  Back-office
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded text-white font-black"
                    style={{ background: '#94a3b8' }}
                  >
                    Bientôt
                  </span>
                </div>
                <h2
                  className="text-base sm:text-lg font-black mt-1 text-foreground/60"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Boîte mail admin
                </h2>
                <p className="text-xs text-foreground/50 mt-1">
                  Nécessite l&apos;activation du MX Resend sur code2roues.fr.
                  Actuellement, <code className="text-[10px] bg-muted px-1 rounded">contact@code2roues.fr</code> est
                  routé vers Zimbra OVH — utilisez le webmail.
                </p>
              </div>
            </div>
          </div>
        )}

        {isAdmin && (
          <button
            onClick={handleSeed}
            className="group text-left rounded-xl border-2 border-border hover:border-[#1d4ed8] bg-card p-5 transition-all hover:shadow-md"
            data-testid="section-seed"
          >
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                style={{ background: '#7c3aed' }}
              >
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">
                  Outils admin
                </div>
                <h2
                  className="text-base sm:text-lg font-black mt-1 group-hover:text-[#1d4ed8] transition-colors"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Seed démo
                </h2>
                <p className="text-xs text-foreground/60 mt-1">
                  Crée un jeu de données de démonstration (1 prof, 1 élève, 1 leçon aujourd&apos;hui). Idempotent.
                </p>
              </div>
              <ArrowRight className="w-4 h-4 mt-1 text-foreground/40 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        )}

        {isAdmin && (
          <button
            onClick={handleSeedVoiture}
            disabled={voitureSeedLoading}
            className="group text-left rounded-xl border-2 border-border hover:border-[#1d4ed8] bg-card p-5 transition-all hover:shadow-md disabled:opacity-60 disabled:cursor-wait"
            data-testid="section-seed-voiture"
          >
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                style={{ background: '#1d4ed8' }}
              >
                <Car className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">
                  Outils admin · Production
                </div>
                <h2
                  className="text-base sm:text-lg font-black mt-1 group-hover:text-[#1d4ed8] transition-colors"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {voitureSeedLoading ? 'Import en cours…' : 'Importer contenu voiture'}
                </h2>
                <p className="text-xs text-foreground/60 mt-1">
                  Peuple la DB avec les 103 articles voiture (14 enquêtes + 9 dossiers + 24 satellites + car_*). Idempotent.
                </p>
              </div>
              <ArrowRight className="w-4 h-4 mt-1 text-foreground/40 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        )}
      </div>

      {seedResult && (
        <div
          className="mt-6 rounded-lg border-2 p-4 text-xs sm:text-sm"
          style={{ borderColor: '#059669', background: 'rgba(5,150,105,0.08)' }}
          data-testid="seed-result"
        >
          <div className="font-black text-foreground mb-1">✓ Seed exécuté</div>
          <pre className="text-[11px] text-foreground/70 overflow-auto">
            {JSON.stringify(seedResult, null, 2)}
          </pre>
        </div>
      )}

      {voitureSeedResult && (
        <div
          className="mt-6 rounded-lg border-2 p-4 text-xs sm:text-sm"
          style={{ borderColor: '#1d4ed8', background: 'rgba(29,78,216,0.08)' }}
          data-testid="voiture-seed-result"
        >
          <div className="font-black text-foreground mb-1">✓ Contenu voiture importé</div>
          <div className="text-foreground/70 mb-2">
            La page <code className="bg-muted px-1 rounded">/voiture</code> devrait maintenant afficher les 9 tuiles ETM et la liste des articles. Rechargez la page pour vérifier.
          </div>
          <pre className="text-[11px] text-foreground/70 overflow-auto max-h-64">
            {JSON.stringify(voitureSeedResult, null, 2)}
          </pre>
        </div>
      )}

      {error && (
        <div
          className="mt-6 rounded-lg border-2 p-4 text-xs sm:text-sm flex items-start gap-2"
          style={{ borderColor: '#dc2626', background: 'rgba(220,38,38,0.08)' }}
          data-testid="bo-error"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-600" />
          <div>{error}</div>
        </div>
      )}

      {/* Retour public */}
      <div className="mt-10 text-center border-t border-border/50 pt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-foreground/60 hover:text-[hsl(var(--primary))]"
          data-testid="back-to-public"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Retourner sur le site public
        </Link>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div
      className="rounded-xl border-2 border-border bg-card p-3 sm:p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-foreground/60">
          {label}
        </div>
      </div>
      <div
        className="text-2xl sm:text-3xl font-black text-foreground"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {value ?? '—'}
      </div>
    </div>
  );
}

function SectionCard({ to, icon: Icon, title, subtitle, testid, badge }) {
  return (
    <Link
      to={to}
      className="group rounded-xl border-2 border-border hover:border-[#1d4ed8] bg-card p-5 transition-all hover:shadow-md"
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white flex-shrink-0"
          style={{ background: BO_BLUE }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 flex items-center gap-2">
            Back-office
            {badge && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded text-white font-black"
                style={{ background: '#f59e0b' }}
              >
                {badge}
              </span>
            )}
          </div>
          <h2
            className="text-base sm:text-lg font-black mt-1 group-hover:text-[#1d4ed8] transition-colors"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {title}
          </h2>
          <p className="text-xs text-foreground/60 mt-1">{subtitle}</p>
        </div>
        <ArrowRight className="w-4 h-4 mt-1 text-foreground/40 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}
